import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Clock, DollarSign, Star } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import VehicleCard from "@/components/VehicleCard";
import { useSite } from "@/lib/site-context";

const benefits = [
  { icon: Sparkles, title: "Easy Booking", desc: "Quick, simple booking with clear rental details." },
  { icon: ShieldCheck, title: "Reliable Vehicles", desc: "Clean, well-maintained vehicles ready for your journey." },
  { icon: Clock, title: "Friendly Service", desc: "Helpful local support when you need it." },
  { icon: DollarSign, title: "Transparent Pricing", desc: "Clear rental rates with no unnecessary surprises." },
];

const testimonials = [
  { name: "Placeholder Reviewer A", role: "Family Traveller", quote: "Add real customer testimonials here from your admin panel or edit this file." },
  { name: "Placeholder Reviewer B", role: "Business Traveller", quote: "Placeholder review — replace with a genuine customer quote." },
  { name: "Placeholder Reviewer C", role: "Tourist", quote: "Placeholder review — replace with a genuine customer quote." },
];

export default function Home() {
  const { vehicles } = useSite();
  const featured = vehicles.slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-90" />
        {/* Subtle bottom vignette to blend into the page */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        {/* Strong LEFT-side overlay for headline legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 lg:pt-24 lg:pb-40">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D9A62E]/40 bg-[#050505]/60 backdrop-blur-md text-[#D9A62E] text-xs font-semibold font-mono-piki uppercase tracking-widest fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D9A62E]" /> Locally Operated in Auckland
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[0.95] fade-up delay-100" data-testid="hero-main-heading">
              Easy, Reliable<br/>Car Rentals<br/><span className="text-[#D9A62E]">in Auckland</span>
            </h1>
            <div className="mt-5 h-[3px] w-24 bg-[#D9A62E] rounded-full fade-up delay-200" />
            <p className="mt-6 text-base sm:text-lg text-[#E5E5E5] max-w-xl leading-relaxed fade-up delay-200">
              Simple, reliable car rentals across Auckland. Clean vehicles, transparent pricing and friendly local service.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 fade-up delay-300">
              <Link to="/book" className="piki-gold-btn inline-flex items-center gap-2" data-testid="hero-book-btn">
                Book a Car <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/fleet" className="piki-outline-btn inline-flex items-center gap-2" data-testid="hero-explore-fleet-btn">
                Browse Fleet
              </Link>
            </div>
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SearchBar />
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="pt-14 pb-20 lg:pt-20 lg:pb-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D9A62E] font-mono-piki font-semibold">Why Choose Piki</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white">Reliable Rentals. Straightforward Service.</h2>
            </div>
            <p className="text-sm text-[#A3A3A3] max-w-md">Clean vehicles, transparent pricing and friendly local service — without the unnecessary hassle.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="piki-card p-6 flex flex-col" data-testid={`benefit-card-${i}`}>
                <div className="w-14 h-14 rounded-xl bg-[#D9A62E]/10 border border-[#D9A62E]/30 flex items-center justify-center">
                  <b.icon className="w-7 h-7 text-[#D9A62E]" />
                </div>
                <div className="mt-5 font-display font-bold text-lg text-white leading-tight whitespace-nowrap">{b.title}</div>
                <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="py-20 lg:py-28 bg-[#050505] border-t border-[#171717]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D9A62E] font-mono-piki font-semibold">Featured Fleet</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white">Popular Vehicles</h2>
            </div>
            <Link to="/fleet" className="text-sm text-[#D9A62E] hover:text-[#C29324] inline-flex items-center gap-1.5 font-semibold">
              View entire fleet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D9A62E] font-mono-piki font-semibold">Customer Reviews</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white">What people say</h2>
            <p className="mt-3 text-sm text-[#737373]">Placeholder testimonials. Replace with real customer reviews.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="piki-card p-6" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#D9A62E] text-[#D9A62E]" />)}
                </div>
                <p className="text-sm text-[#D4D4D4] leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-[#2E2E2E]">
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-[#737373]">{t.role} · Placeholder</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-20 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#D9A62E] to-[#C29324] px-8 py-12 sm:px-14 sm:py-14 text-center">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#050505] tracking-tight">Ready to book your car?</h2>
            <p className="mt-4 text-[#171717] text-base sm:text-lg max-w-xl mx-auto">Choose your vehicle and send us your rental dates today.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/book" className="bg-[#050505] text-white px-6 py-3.5 rounded-xl font-bold uppercase tracking-wider hover:bg-[#171717] transition-colors" data-testid="cta-book-btn">Book Now</Link>
              <Link to="/contact" className="bg-white text-[#050505] px-6 py-3.5 rounded-xl font-bold uppercase tracking-wider hover:bg-[#F7F7F7] transition-colors" data-testid="cta-contact-btn">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
